import {
  collection, doc, getDocs, addDoc, updateDoc, deleteDoc,
  serverTimestamp, query, where
} from 'firebase/firestore';
import { db } from './firebase';
import { Equipment, Category, EquipmentItemType } from '../types';
import { logAuditEvent } from './auditService';
import { equipmentTracking, DateTrackingSettings } from '../utils/categoryTracking';

function withCategoryTracking(items: Equipment[], categories: Category[]): Equipment[] {
  const byId = new Map(categories.map(category => [category.id, category]));
  return items.map(item => ({
    ...item,
    ownTracking: { tracksExpiry: item.tracksExpiry === true, tracksReview: item.tracksReview === true },
    ...equipmentTracking(item, byId),
  }));
}

export async function getCategories(): Promise<Category[]> {
  const snap = await getDocs(collection(db, 'categories'));
  const cats = snap.docs.map(d => {
    const data = d.data();
    return {
      id: d.id,
      name: data.name,
      description: data.description,
      parentId: data.parentId ?? null,
      weight: data.weight ?? 0,
      requiresActionNumber: data.requiresActionNumber ?? false,
      tracksExpiry: data.tracksExpiry === true,
      tracksReview: data.tracksReview === true,
      createdAt: data.createdAt?.toDate?.() ?? new Date(),
    };
  });
  return cats.sort((a, b) => a.weight - b.weight);
}

export async function createCategory(name: string, description?: string, parentId?: string | null, weight: number = 0, requiresActionNumber: boolean = false, tracking: DateTrackingSettings = {}): Promise<string> {
  const ref = await addDoc(collection(db, 'categories'), {
    name,
    description: description ?? '',
    parentId: parentId ?? null,
    weight,
    requiresActionNumber,
    tracksExpiry: tracking.tracksExpiry === true,
    tracksReview: tracking.tracksReview === true,
    createdAt: serverTimestamp(),
  });
  logAuditEvent({ entityType: 'category', entityId: ref.id, action: 'create', details: name }).catch(() => {});
  return ref.id;
}

export async function updateCategory(id: string, name: string, description?: string, parentId?: string | null, weight: number = 0, requiresActionNumber: boolean = false, tracking?: DateTrackingSettings): Promise<void> {
  const update: Record<string, unknown> = { name, description: description ?? '', weight, requiresActionNumber };
  if (tracking) {
    update.tracksExpiry = tracking.tracksExpiry === true;
    update.tracksReview = tracking.tracksReview === true;
  }
  if (parentId !== undefined) {
    update.parentId = parentId;
  }
  await updateDoc(doc(db, 'categories', id), update);
  logAuditEvent({ entityType: 'category', entityId: id, action: 'edit', details: name }).catch(() => {});
}

export async function deleteCategory(id: string): Promise<void> {
  const invQ = query(collection(db, 'inventory'), where('categoryId', '==', id));
  const invSnap = await getDocs(invQ);
  if (!invSnap.empty) {
    throw new Error('Cannot delete category: equipment items are linked to it.');
  }
  const subQ = query(collection(db, 'categories'), where('parentId', '==', id));
  const subSnap = await getDocs(subQ);
  if (!subSnap.empty) {
    throw new Error('Cannot delete category: it has subcategories.');
  }
  await deleteDoc(doc(db, 'categories', id));
  logAuditEvent({ entityType: 'category', entityId: id, action: 'delete' }).catch(() => {});
}

export async function getEquipmentByCategory(categoryId: string): Promise<Equipment[]> {
  const q = query(collection(db, 'inventory'), where('categoryId', '==', categoryId));
  const snap = await getDocs(q);
  const categories = await getCategories();
  return withCategoryTracking(snap.docs.map((d): Equipment => {
    const data = d.data();
    return {
      id: d.id,
      name: data.name,
      categoryId: data.categoryId,
      unit: data.unit,
      description: data.description,
      minQuantity: data.minQuantity ?? 0,
      itemType: data.itemType === 'reusable' ? 'reusable' : 'disposable',
      tracksExpiry: data.tracksExpiry ?? false,
      tracksReview: data.tracksReview ?? false,
      createdAt: data.createdAt?.toDate?.() ?? new Date(),
    };
  }).sort((a, b) => a.name.localeCompare(b.name)), categories);
}

export async function getEquipment(): Promise<Equipment[]> {
  const snap = await getDocs(collection(db, 'inventory'));
  const categories = await getCategories();
  return withCategoryTracking(snap.docs.map((d): Equipment => {
    const data = d.data();
    return {
      id: d.id,
      name: data.name,
      categoryId: data.categoryId,
      unit: data.unit,
      description: data.description,
      minQuantity: data.minQuantity ?? 0,
      itemType: data.itemType === 'reusable' ? 'reusable' : 'disposable',
      tracksExpiry: data.tracksExpiry ?? false,
      tracksReview: data.tracksReview ?? false,
      createdAt: data.createdAt?.toDate?.() ?? new Date(),
    };
  }).sort((a, b) => a.name.localeCompare(b.name)), categories);
}

export async function createEquipment(name: string, categoryId: string, unit: string, description?: string, minQuantity: number = 0, tracking: Pick<Equipment, 'tracksExpiry' | 'tracksReview'> = {}, itemType: EquipmentItemType = 'disposable'): Promise<string> {
  if (!['disposable', 'reusable'].includes(itemType)) throw new Error('INVALID_ITEM_TYPE');
  const ref = await addDoc(collection(db, 'inventory'), {
    name,
    categoryId,
    unit,
    description: description ?? '',
    minQuantity,
    itemType,
    tracksExpiry: tracking.tracksExpiry === true,
    tracksReview: tracking.tracksReview === true,
    createdAt: serverTimestamp(),
  });
  logAuditEvent({ entityType: 'equipment', entityId: ref.id, action: 'create', details: name }).catch(() => {});
  return ref.id;
}

export async function updateEquipment(id: string, name: string, categoryId: string, unit: string, description?: string, minQuantity: number = 0, tracking?: Pick<Equipment, 'tracksExpiry' | 'tracksReview'>, itemType?: EquipmentItemType): Promise<void> {
  if (itemType !== undefined && !['disposable', 'reusable'].includes(itemType)) throw new Error('INVALID_ITEM_TYPE');
  await updateDoc(doc(db, 'inventory', id), { name, categoryId, unit, description: description ?? '', minQuantity, ...(itemType ? { itemType } : {}), ...(tracking ? {tracksExpiry: tracking.tracksExpiry === true, tracksReview: tracking.tracksReview === true} : {}) });
  logAuditEvent({ entityType: 'equipment', entityId: id, action: 'edit', details: name }).catch(() => {});
}

export async function deleteEquipment(id: string): Promise<void> {
  await deleteDoc(doc(db, 'inventory', id));
  logAuditEvent({ entityType: 'equipment', entityId: id, action: 'delete' }).catch(() => {});
}

